import { ref, computed } from 'vue'
import {
    OfferDetails,
    Welcome,
    Login,
    MakeOffer,
    Register,
    ArtistInfo,
    UploadMedia,
    OrganizeMedia,
    OfferSent,
    AuthedOfferSent,
    BioPayout,
    Socials,
    Final,
    VenueWarning,
    ChooseArtist
} from '@/components/artist-direct-booking'

import Cropper from '@/components/ui/images/Cropper.vue'

const step = ref(0)

export const useArtistDirectBookingSteps = (
    isAuthed,
    event,
    opportunityInfo,
    editImage,
    social,
    userData,
    validation,
    profilePicIndx,
    isEditImgAvatar,
    croppedImage,
    artists,
    load
) => {
    const steps = ref([
        {
            identifier: 'offer-details',
            title: 'Make an Offer',
            component: OfferDetails,
            actions: {
                continue: {
                    authed: {
                        venue: 'venue-warning',
                        artist: 'make-offer'
                    },
                    guest: 'welcome'
                },
                back: null,
                clear: null,
                skippable: false
            },
            needValidation: false,
            props: {
                opportunityInfo: opportunityInfo,
                authed: isAuthed
            }
        },
        {
            identifier: 'choose-artist',
            title: 'Make an Offer',
            component: ChooseArtist,
            actions: {
                continue: 'make-offer',
                back: 'offer-details',
                clear: null,
                skippable: false
            },
            needValidation: false,
            props: {
                artists: artists,
                load: load
            }
        },
        {
            identifier: 'welcome',
            title: 'Make an Offer',
            component: Welcome,
            actions: {
                continue: {
                    authed: 'login',
                    guest: 'make-offer'
                },
                back: 'offer-details',
                clear: {
                    keys: 'welcome'
                },
                skippable: false
            },
            needValidation: true,
            props: {
                redirectStorageData: {
                    'direct-booking-modal': true,
                    'direct-booking-step': 'make-offer',
                    'direct-booking-event': event
                },
                userData: userData,
                validation: validation,
                social: social,
                actions: {
                    continue: {
                        authed: 'login',
                        guest: 'make-offer'
                    },
                    back: 'offer-details',
                    clear: {
                        keys: 'welcome'
                    },
                    skippable: false
                }
            }
        },
        {
            identifier: 'login',
            title: 'Make an Offer',
            component: Login,
            actions: {
                continue: 'choose-artist',
                back: null,
                clear: {
                    keys: 'login'
                },
                skippable: false
            },
            needValidation: true,
            props: {
                event: event,
                redirectStorageData: {
                    'direct-booking-modal': true,
                    'direct-booking-step': 'make-offer',
                    'direct-booking-event': event
                },
                userData: userData,
                social: social,
                actions: {
                    continue: 'choose-artist',
                    back: null,
                    clear: {
                        keys: 'login'
                    },
                    skippable: false
                }
            }
        },
        {
            identifier: 'make-offer',
            title: 'Make an Offer',
            component: MakeOffer,
            actions: {
                continue: {
                    authed: {
                        complete: 'authed-offer-sent',
                        missingBooking: 'artist-info',
                        missingMedia: 'upload-media',
                        social: 'artist-info',
                        venue: null
                    },
                    guest: 'register'
                },
                back: {
                    authed: 'offer-details',
                    guest: 'welcome'
                },
                clear: {
                    keys: 'makeOffer'
                },
                skippable: false
            },
            needValidation: true,
            props: {
                opportunityInfo: opportunityInfo,
                userData: userData,
                validation: validation
            }
        },
        {
            identifier: 'register',
            title: 'Make an Offer',
            component: Register,
            actions: {
                continue: 'artist-info',
                back: 'make-offer',
                clear: {
                    keys: 'register'
                },
                skippable: false
            },
            needValidation: true,
            props: {
                userData: userData,
                validation: validation
            }
        },
        {
            identifier: 'artist-info',
            title: 'Make an Offer',
            component: ArtistInfo,
            actions: {
                continue: 'upload-media',
                back: {
                    authed: 'make-offer',
                    guest: null
                },
                clear: {
                    keys: 'artistInfo'
                },
                skippable: false
            },
            needValidation: true,
            props: {
                userData: userData,
                validation: validation
            }
        },
        {
            identifier: 'upload-media',
            title: 'Make an Offer',
            component: UploadMedia,
            actions: {
                continue: 'organize-media',
                back: 'artist-info',
                clear: {
                    keys: ['images', 'videos', 'audios']
                },
                skippable: false
            },
            needValidation: false,
            props: {
                userData: userData
            }
        },
        {
            identifier: 'organize-media',
            title: 'Make an Offer',
            component: OrganizeMedia,
            actions: {
                continue: 'offer-sent',
                back: 'upload-media',
                clear: null,
                skippable: false
            },
            needValidation: false,
            props: {
                userData: userData,
                profilePicIndx: profilePicIndx
            }
        },
        {
            identifier: 'offer-sent',
            title: 'Make an Offer',
            component: OfferSent,
            actions: {
                continue: 'bio-payout',
                back: null,
                clear: null,
                skippable: true
            },
            needValidation: false,
            props: {
                userData: userData,
                opportunityInfo: opportunityInfo
            }
        },
        {
            identifier: 'authed-offer-sent',
            title: 'Make an Offer',
            component: AuthedOfferSent,
            actions: {
                continue: 'close',
                back: null,
                clear: null,
                skippable: false
            },
            needValidation: false,
            props: {
                opportunityInfo: opportunityInfo
            }
        },
        {
            identifier: 'venue-warning',
            title: 'Make an Offer',
            component: VenueWarning,
            actions: {
                continue: 'close',
                back: null,
                clear: null,
                skippable: false
            },
            needValidation: false,
            props: {}
        },
        {
            identifier: 'bio-payout',
            title: 'Make an Offer',
            component: BioPayout,
            actions: {
                continue: 'socials',
                back: 'offer-sent',
                clear: {
                    keys: 'bioPayout'
                },
                skippable: false
            },
            needValidation: true,
            props: {
                userData: userData,
                validation: validation
            }
        },
        {
            identifier: 'socials',
            title: 'Make an Offer',
            component: Socials,
            actions: {
                continue: 'final',
                back: 'bio-payout',
                clear: {
                    keys: ['socials', 'socialInfo']
                },
                skippable: false
            },
            needValidation: true,
            props: {
                userData: userData,
                validation: validation
            }
        },
        {
            identifier: 'final',
            title: 'Make an Offer',
            component: Final,
            actions: {
                continue: 'close',
                back: null,
                clear: null,
                skippable: false
            },
            needValidation: false,
            props: {
                userData: userData
            }
        },
        {
            identifier: 'edit-photo',
            title: 'Edit Profile Picture',
            component: Cropper,
            actions: {
                continue: 'organize-media',
                back: 'organize-media',
                clear: null,
                skippable: false
            },
            needValidation: false,
            props: {
                image: editImage,
                isAvatar: isEditImgAvatar,
                croppedImage: croppedImage
            }
        }
    ])
    function updateStep(identifier) {
        if (!identifier) {
            console.error('Incorrect step identifier in artist booking.')
            return
        }
        step.value = steps.value.indexOf(steps.value.find((step) => step.identifier === identifier))
    }

    const currentStep = computed(() => steps.value[step.value])
    return { step, steps, currentStep, updateStep }
}
